import { type RefObject, useCallback, useContext, useEffect } from "react";
import { Context } from "../../provider";

export default function useOutsideClick(
	ref: RefObject<HTMLElement | null>[],
	callback: (e: MouseEvent) => void,
) {
	const root = useContext(Context);
	if (!root) {
		throw new Error("Root not found");
	}

	const handleClickOutside: EventListener = useCallback(
		(event: Event) => {
			const mouseEvent = event as MouseEvent;
			const isRefGuard = (
				_ref: RefObject<HTMLElement | null>,
			): _ref is RefObject<HTMLElement> => !!_ref?.current;
			const availableRefs = ref.filter(isRefGuard);
			if (
				availableRefs.every((_ref) => _ref.current) &&
				!availableRefs.some((_ref) =>
					_ref.current.contains(mouseEvent.target as Node),
				)
			) {
				callback(mouseEvent);
			}
		},
		[ref, callback],
	);

	useEffect(() => {
		const eventTarget = root.shadowRoot || root;
		eventTarget.addEventListener("mousedown", handleClickOutside);
		return () => {
			eventTarget.removeEventListener("mousedown", handleClickOutside);
		};
	}, [handleClickOutside, root]);
}
